import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

//ui component
import IconButton from '@material-ui/core/IconButton';
import { default as ImageComponent } from './Image';
import { CircularProgress } from '@material-ui/core';

//utils
import makeStyles from '@material-ui/core/styles/makeStyles';
import { saveAs } from 'file-saver';
//assets
import Trash from '../../../assets/images/trash.svg';
import Rotate from '../../../assets/images/rotate-left.svg';
import Download from '../../../assets/images/download.svg';
import AlertDialog from '../Dialogs/AlertDialog';
import MessagesContext from '../../../contexts/messagesContext';

const useStyles = makeStyles((theme) => ({
    container: {
        position: 'relative',
        overflow: 'hidden',
        minWidth: (props) => (props.isRadiology ? '370px' : '500px'),
        width: '100%',
        flex: (props) => (props.isRadiology ? '0 1' : 'unset'),
        borderRadius: 9,
        border: '2px solid #dadada',
        direction: 'ltr',

        [theme.breakpoints.down('600')]: {
            minWidth: '440px !important'
        },
        [theme.breakpoints.down('500')]: {
            minWidth: '365px !important'
        },
        [theme.breakpoints.down('400')]: {
            minWidth: '320px !important'
        },
        [theme.breakpoints.down('350')]: {
            minWidth: '260px !important'
        }
    },
    image: {
        width: '100%',
        height: '100%',
        minHeight: 200
    },
    actionBar: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%'
    },
    icon: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 30,
        height: 30,
        padding: 0,
        margin: '2px',
        '& img': {
            maxWidth: 15
        }
    }
}));

/**
 *
 * @param {{src: string,className: string,classes:{image: string}, onImageEdited: () => void,onClick: () => void,handleDelete: () => void,disableEditing: boolean,deleteLoading: boolean}} props
 */
export default function ImageEditor({
    src,
    onImageEdited,
    onClick,
    handleDelete,
    disableEditing,
    deleteLoading,
    isAddRadiology,
    isRadiology,
    ...props
}) {
    const { messages } = useContext(MessagesContext);
    const classes = useStyles({ isAddRadiology, isRadiology });
    const [source, setSource] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);

    useEffect(() => {
        setSource(src);
    }, [src]);

    const deleteHandler = useCallback(() => {
        setDeleteDialog(false);
        handleDelete && handleDelete();
    }, [handleDelete]);

    const handleImageRotation = useCallback(() => {
        // if (!fileLoaded) return;
        const image = new Image();
        image.crossOrigin = 'anonymous';
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const degree = (90 * Math.PI) / 180;

        image.onload = () => {
            canvas.width = image.height;
            canvas.height = image.width;
            ctx.save();
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(degree);
            ctx.drawImage(image, -image.width / 2, -image.height / 2);
            const base = canvas.toDataURL('image/jpeg');
            onImageEdited && onImageEdited(base);
        };

        image.src = source;
    }, [source, onImageEdited]);

    const handleDownload = useCallback(() => {
        saveAs(src);
    }, [src]);

    const handleOpenDeleteDialog = useCallback(() => {
        setDeleteDialog(true);
    }, []);
    const handleCloseDeleteDialog = useCallback(() => {
        setDeleteDialog(false);
    }, []);

    return (
        <>
            <div {...props} className={`${classes.container} ${props.className}`}>
                <ImageComponent
                    src={source}
                    className={`${classes.image} ${props.classes?.image}`}
                    onClick={onClick}
                    alt="image to edit"
                    data-testid="image"
                />
                <div className={classes.actionBar}>
                    <IconButton
                        onClick={handleOpenDeleteDialog}
                        size="medium"
                        className={classes.icon}
                        data-testid="trashImage"
                    >
                        {deleteLoading ? (
                            <CircularProgress size="15px" />
                        ) : (
                            <>
                                <img src={Trash} alt="trash" />
                            </>
                        )}
                    </IconButton>
                    {!disableEditing && (
                        <IconButton
                            onClick={handleImageRotation}
                            size="medium"
                            className={classes.icon}
                        >
                            <img src={Rotate} alt="rotate" />
                        </IconButton>
                    )}

                    <IconButton onClick={handleDownload} size="medium" className={classes.icon}>
                        <img src={Download} alt="download" />
                    </IconButton>
                </div>
            </div>
            <AlertDialog
                title={messages.alerts.deleteImageTitle}
                alertText={messages.alerts.deleteImageHint}
                handleClose={handleCloseDeleteDialog}
                handleAction={deleteHandler}
                confirmText={messages.yes}
                cancelText={messages.no}
                open={deleteDialog}
            />
        </>
    );
}

ImageEditor.propTypes = {
    src: PropTypes.string,
    className: PropTypes.string,
    onImageEdited: PropTypes.func,
    onClick: PropTypes.func,
    handleDelete: PropTypes.func,
    disableEditing: PropTypes.bool,
    deleteLoading: PropTypes.bool,
    classes: PropTypes.object,
    isAddRadiology: PropTypes.bool,
    isRadiology: PropTypes.bool
};
